import { useCallback, useMemo, useState } from 'react';
import type { IDataTableColumn } from '@srm/ui/data-table';

export interface IColumnSet {
    id: string;
    name: string;
    fields: string[];
    own?: boolean;
    /** Схема, на которой пользователь сохранил набор. Старые наборы не включаем автоматически. */
    schema?: string;
}

export const COLUMN_SETS_STORAGE_PREFIX = 'table-column-sets.v2.';

interface IStored {
    schema: string;
    own: IColumnSet[];
    activeId: string;
    overrides: Record<string, boolean>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);
const isStrings = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((field) => typeof field === 'string');
const sameFields = (a: readonly string[], b: readonly string[]) =>
    a.length === b.length && a.every((field) => b.includes(field));

const readStored = (key: string | undefined): IStored | undefined => {
    if (!key) return;
    try {
        const value: unknown = JSON.parse(localStorage.getItem(COLUMN_SETS_STORAGE_PREFIX + key) ?? 'null');
        if (
            !isRecord(value) ||
            typeof value.schema !== 'string' ||
            typeof value.activeId !== 'string' ||
            !Array.isArray(value.own) ||
            !isRecord(value.overrides) ||
            !Object.values(value.overrides).every((visible) => typeof visible === 'boolean')
        )
            return;
        const ids = new Set<string>();
        const own: IColumnSet[] = [];
        for (const set of value.own) {
            if (
                !isRecord(set) ||
                typeof set.id !== 'string' ||
                !set.id.startsWith('own-') ||
                ids.has(set.id) ||
                typeof set.name !== 'string' ||
                !isStrings(set.fields) ||
                typeof set.schema !== 'string'
            )
                continue;
            ids.add(set.id);
            own.push({ id: set.id, name: set.name, fields: [...new Set(set.fields)], own: true, schema: set.schema });
        }
        return {
            schema: value.schema,
            activeId: value.activeId,
            own,
            overrides: value.overrides as Record<string, boolean>,
        };
    } catch {
        return;
    }
};

const writeStored = (key: string | undefined, value: IStored) => {
    if (!key) return;
    try {
        localStorage.setItem(COLUMN_SETS_STORAGE_PREFIX + key, JSON.stringify(value));
    } catch {
        // В приватном режиме/при переполнении выбор продолжает работать в памяти.
    }
};

/** Только состав схемы: ширины, подписи и результаты формул видимости сюда не входят. */
export const columnSetsSchema = (columns: IDataTableColumn<never>[], builtin: IColumnSet[], identity = '') =>
    JSON.stringify([
        identity,
        columns
            .map((column) => [column.field, column.dataType ?? null])
            .sort(([a], [b]) => String(a).localeCompare(String(b))),
        builtin.map((set) => [set.id, [...set.fields].sort()]).sort(([a], [b]) => String(a).localeCompare(String(b))),
    ]);

const reconcile = (stored: IStored | undefined, schema: string, builtin: IColumnSet[]): IStored => {
    const own = stored?.own.filter((set) => !builtin.some((preset) => preset.id === set.id)) ?? [];
    const active = [...builtin, ...own].find((set) => set.id === stored?.activeId);
    const validActive = active && (!active.own || active.schema === schema);
    return {
        schema,
        own,
        activeId: validActive ? active.id : (builtin[0]?.id ?? ''),
        overrides: validActive && stored?.schema === schema ? stored.overrides : {},
    };
};

/** Пресет хранится как ID, ручные отклонения — отдельно. v1 намеренно не импортируется:
 * в нём нет владельца формы/пользователя, и запись могла быть сохранена другой таблицей. */
export const useColumnSets = (
    storageKey: string | undefined,
    columns: IDataTableColumn<never>[],
    builtin: IColumnSet[],
    enabled: boolean,
    schemaIdentity = '',
) => {
    const allFields = useMemo(() => columns.map((column) => column.field), [columns]);
    const availableFields = useMemo(
        () => columns.filter((column) => column.visible !== false).map((column) => column.field),
        [columns],
    );
    const schema = useMemo(
        () => columnSetsSchema(columns, builtin, schemaIdentity),
        [columns, builtin, schemaIdentity],
    );
    const key = enabled ? storageKey : undefined;
    const [bound, setBound] = useState(() => ({ key, enabled, value: reconcile(readStored(key), schema, builtin) }));
    // Смена владельца обрабатывается до рендера детей: ни одного кадра/обработчика со старим выбором.
    const value =
        bound.key !== key || bound.enabled !== enabled
            ? reconcile(readStored(key), schema, builtin)
            : bound.value.schema !== schema
              ? reconcile(bound.value, schema, builtin)
              : bound.value;
    if (value !== bound.value) setBound({ key, enabled, value });

    const sets = useMemo(() => [...builtin, ...value.own], [builtin, value.own]);
    const activeSet = sets.find((set) => set.id === value.activeId);
    const baseFields = activeSet?.fields ?? allFields;
    const picked = allFields.filter((field) =>
        Object.hasOwn(value.overrides, field) ? value.overrides[field] : baseFields.includes(field),
    );
    const pickedKey = JSON.stringify(picked);
    const columnVisibility = useMemo(
        () => (enabled ? Object.fromEntries(allFields.map((field) => [field, picked.includes(field)])) : undefined),
        // pickedKey represents the selection independently of callback/column object identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [enabled, allFields, pickedKey],
    );
    const dirty = !sameFields(
        baseFields.filter((field) => availableFields.includes(field)),
        picked.filter((field) => availableFields.includes(field)),
    );
    const commit = useCallback(
        (next: IStored) => {
            setBound({ key, enabled, value: next });
            if (enabled) writeStored(key, next);
        },
        [key, enabled],
    );

    const pick = (fields: string[]) => {
        const overrides = Object.fromEntries(
            allFields
                .filter((field) => fields.includes(field) !== baseFields.includes(field))
                .map((field) => [field, fields.includes(field)]),
        );
        commit({ ...value, overrides });
    };
    const onColumnVisibilityChange = (visibility: Record<string, boolean>) => {
        pick(
            allFields.filter((field) =>
                availableFields.includes(field) ? visibility[field] !== false : picked.includes(field),
            ),
        );
    };
    const revealFields = (fields: string[]) => {
        if (fields.length) pick(allFields.filter((field) => fields.includes(field) || picked.includes(field)));
    };
    const applySet = (id: string) => {
        const set = sets.find((candidate) => candidate.id === id);
        if (!set) return;
        // Старый собственный набор можно включить только явным выбором пользователя.
        if (set.own && set.fields.length && !set.fields.some((field) => allFields.includes(field))) return;
        const own = value.own.map((candidate) => (candidate.id === id ? { ...candidate, schema } : candidate));
        commit({ ...value, own, activeId: id, overrides: {} });
    };
    const resetSet = () => commit({ ...value, overrides: {} });
    const saveAsNew = (name: string) => {
        const id = `own-${crypto.randomUUID()}`;
        commit({
            ...value,
            own: [...value.own, { id, name, fields: picked, own: true, schema }],
            activeId: id,
            overrides: {},
        });
        return id;
    };
    const updateActive = () => {
        if (!activeSet?.own) return;
        commit({
            ...value,
            own: value.own.map((set) => (set.id === activeSet.id ? { ...set, fields: picked, schema } : set)),
            overrides: {},
        });
    };
    const renameActive = (name: string) => {
        if (!activeSet?.own) return;
        commit({ ...value, own: value.own.map((set) => (set.id === activeSet.id ? { ...set, name } : set)) });
    };
    const deleteActive = () => {
        if (!activeSet?.own) return;
        commit({
            ...value,
            own: value.own.filter((set) => set.id !== activeSet.id),
            activeId: builtin[0]?.id ?? '',
            overrides: {},
        });
    };

    return {
        sets,
        activeId: value.activeId,
        activeSet,
        dirty,
        pickedCount: picked.length,
        totalCount: allFields.length,
        columnVisibility,
        onColumnVisibilityChange,
        revealFields,
        applySet,
        resetSet,
        saveAsNew,
        updateActive,
        renameActive,
        deleteActive,
    };
};
